// @seventide/types — platform area: offline (NFR "Offline", ADR 0004). Re-exported by ../platform.ts.
//
// Offline write convention shared by the web outbox and every module's create/update endpoints:
//  - Creates carry a client-generated UUID as the record `id` in the body (also sent as the `Idempotency-Key`
//    header). The endpoint is idempotent on (workspace_id, id/client_id): a retried POST returns the existing
//    record (200) instead of creating a duplicate (201).
//  - Updates carry `clientUpdatedAt` (when the edit was made on the device) in the body and/or the
//    `x-client-updated-at` header. Last write wins; the losing version is kept in `entity_history`.
//  - A 200 response with `conflict: true` means the server kept a newer version and stored the device's
//    (older) edit in the change history.
import { z } from 'zod';
import { Id, IsoDateTime } from '../common';

/** Request headers used by the offline outbox (lower-case, as Node/Fastify exposes them). */
export const OFFLINE_HEADERS = {
  /** Equals the client-generated record id for creates; a per-operation UUID for updates/deletes. */
  idempotencyKey: 'idempotency-key',
  /** ISO timestamp of when the change was made on the device (LWW comparison). */
  clientUpdatedAt: 'x-client-updated-at',
  /** Optional: the server `updatedAt` the device last saw (detects concurrent edits that were overwritten). */
  baseUpdatedAt: 'x-base-updated-at',
  /** Set to "1" when the request is replayed from the outbox (diagnostics only). */
  replay: 'x-offline-replay',
} as const;

/** Entity types the web outbox may hold (ADR 0004 §2). Modules may queue other types too. */
export const OFFLINE_ENTITY_TYPES = ['health_entry', 'care_needs_entry', 'gift', 'transaction'] as const;
export type OfflineEntityType = (typeof OFFLINE_ENTITY_TYPES)[number];

/** Plain-English, content-free labels for queued items ("Gift — saved on 12 Oct 2026"). */
export const OFFLINE_ENTITY_LABELS: Record<string, string> = {
  health_entry: 'Care diary entry',
  care_needs_entry: 'Care needs diary entry',
  gift: 'Gift',
  transaction: 'Payment or transaction',
  account: 'Account',
  recipient: 'Gift recipient',
  decision: 'Decision record',
  document: 'Document',
};

/** Fields an offline-capable create request may carry (merge into the module's own request schema). */
export const OfflineCreateFields = z.object({
  /** Client-generated UUID used as the record id. */
  id: Id.optional(),
  /** Alias accepted by some modules (health, money); same value as `id`. */
  clientId: Id.optional(),
});
export type OfflineCreateFields = z.infer<typeof OfflineCreateFields>;

/** Fields an offline-capable update request may carry. */
export const OfflineUpdateFields = z.object({
  clientUpdatedAt: IsoDateTime.optional(),
});
export type OfflineUpdateFields = z.infer<typeof OfflineUpdateFields>;

/** GET /api/w/:id/platform/sync/status */
export const OfflineSyncStatusResponse = z.object({
  /** Server clock, so the device can correct `clientUpdatedAt` for clock drift. */
  serverTime: IsoDateTime,
  /** Changes kept in the history because of an offline conflict in the last 30 days (visible modules only). */
  conflictsLast30Days: z.number().int().nonnegative(),
});
export type OfflineSyncStatusResponse = z.infer<typeof OfflineSyncStatusResponse>;

/** GET /api/w/:id/platform/sync/conflicts */
export const OfflineSyncConflictsQuery = z.object({
  since: IsoDateTime.optional(),
  /** Only conflicts caused by (or affecting) my own offline edits. */
  mine: z
    .preprocess((v) => (v === 'true' || v === '1' ? true : v === 'false' || v === '0' ? false : v), z.boolean())
    .optional(),
  limit: z.coerce.number().int().min(1).max(100).default(20),
  offset: z.coerce.number().int().min(0).default(0),
});
export type OfflineSyncConflictsQuery = z.input<typeof OfflineSyncConflictsQuery>;

/** A change kept in the history because of an offline conflict. Metadata only — never the content. */
export const OfflineSyncConflictDto = z.object({
  id: Id,
  module: z.string(),
  entityType: z.string(),
  entityId: Id,
  op: z.enum(['create', 'update', 'delete']),
  /** When the losing change was made on the device (null if unknown). */
  clientUpdatedAt: IsoDateTime.nullable(),
  serverReceivedAt: IsoDateTime,
  /** True when the calling user made the change that was recorded. */
  byMe: z.boolean(),
  /** Plain-English description, e.g. "Gift changed on another device — previous version kept". */
  description: z.string(),
});
export type OfflineSyncConflictDto = z.infer<typeof OfflineSyncConflictDto>;

export const OfflineSyncConflictList = z.object({
  items: z.array(OfflineSyncConflictDto),
  total: z.number().int(),
  limit: z.number().int(),
  offset: z.number().int(),
});
export type OfflineSyncConflictList = z.infer<typeof OfflineSyncConflictList>;
